/**
 * Compute all BRD-defined analytics metrics from raw booking rows.
 *
 * Fields returned by the analytics endpoint:
 *   customer, booking_id, source_booking_id, partner_name,
 *   service_name, service_category_name, service_type_name,
 *   vendor_name, provider, location, service_date, service_start_time,
 *   booking_status, payment_status, price, tax, discount,
 *   service_amount, received_amount, service_location, time_required,
 *   buffer_time_required, service_provider, service_provider_name,
 *   crew_start_time, crew_end_time, crew_break_start_time, crew_break_end_time
 */

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const ORDERED_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const bst = (row) => (row.booking_status || "").toString().toLowerCase().replace(/[_\-\s]/g, "");
const toNum = (v) => parseFloat(v) || 0;
const sum = (arr, key) => arr.reduce((s, r) => s + toNum(r[key]), 0);
const fixed = (n, d = 1) => parseFloat(n.toFixed(d));

const isCompleted = (r) => bst(r).includes("completed");
const isCancelled = (r) => bst(r).includes("cancelled");
const isNoShow = (r) => bst(r) === "noshow" || bst(r).includes("noshow");

const providerKey = (r) => r.provider;

// Returns true if value is a pure number (i.e. an ID, not a name)
const isNumeric = (v) => v != null && String(v).trim() !== "" && !isNaN(Number(String(v).trim()));

// service_date comes back from the ezp_analytics stored procedure as a raw
// mysql2 DATE value. mysql2 (no `dateStrings`/`typeCast` configured) builds
// that as a JS Date at LOCAL midnight on the server, and res.json() then
// serializes it via toISOString() — which converts to UTC. In any timezone
// ahead of UTC (IST, Gulf, etc.) that rolls local midnight back to the
// previous UTC day, e.g. "2026-08-03" (Monday) arrives as
// "2026-08-02T18:30:00.000Z". Naively slicing the first 10 characters of
// that string reads the WRONG (UTC) calendar day — a Monday booking would
// bucket under Sunday. Parsing the full string and reading back with LOCAL
// getters (getDay/getFullYear/etc.) reverses that UTC conversion and
// recovers the original calendar date, provided the browser is in the same
// timezone as the business — the same assumption the rest of this app
// already makes everywhere else dates are handled. A bare "YYYY-MM-DD"
// string (no time/zone component) has no such round-trip to reverse, so
// it's parsed directly as local midnight.
const localServiceDate = (serviceDate) => {
  if (!serviceDate) return null;
  const raw = String(serviceDate);
  const d = /T|\s\d{2}:\d{2}/.test(raw) ? new Date(raw) : new Date(raw.slice(0, 10) + "T00:00:00");
  return isNaN(d.getTime()) ? null : d;
};
const pad2 = (n) => String(n).padStart(2, "0");
const localYMD = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

/**
 * providerName — resolves best human-readable crew name from a row.
 *
 * Priority:
 *   1. service_provider_name  ← NEW: dedicated name field from API
 *   2. crew_name              ← if backend sends this
 *   3. service_provider       ← only if NOT a pure number
 *   4. provider_name          ← alternate field
 *   5. partner_name           ← fallback
 *   6. "Crew #<provider>"     ← last resort
 */
const providerName = (r) => {
  const candidates = [
    r.service_provider_name,
    r.crew_name,
    r.service_provider,
    r.provider_name,
    r.partner_name,
  ];

  for (const v of candidates) {
    if (v != null && String(v).trim() !== "" && !isNumeric(v)) {
      return String(v).trim();
    }
  }

  return r.provider ? `Crew #${r.provider}` : "Unknown Crew";
};

// FR-TR-03: compute the hour a booking ends (start + time_required + buffer)
const getCompletionHour = (row) => {
  if (!row.service_start_time) return null;
  const parts = row.service_start_time.split(":");
  const startMinutes = parseInt(parts[0], 10) * 60 + parseInt(parts[1] || 0, 10);
  const duration = toNum(row.time_required) + toNum(row.buffer_time_required);
  return Math.floor((startMinutes + duration) / 60) % 24;
};

export function computeMetrics(rows = []) {
  if (!rows.length) return null;

  // The stored procedure returns one row per service-offering line inside a booking.
  // Deduplicate by source_booking_id (= group_id) so every booking group counts once
  // for status-based counters (totalBookings, completed, cancelled, no-shows).
  // Revenue rows are NOT deduplicated — each line contributes its own received_amount.
  const toGroupKey = (r) => r.source_booking_id ?? r.booking_id;
  const seenGroups = new Set();
  const uniqueGroupRows = rows.filter((r, idx) => {
    const k = toGroupKey(r);
    // Use a synthetic index key when the row has no booking ID so it still counts
    const sk = (k != null && k !== "") ? String(k) : `__row_${idx}`;
    if (seenGroups.has(sk)) return false;
    seenGroups.add(sk);
    return true;
  });

  const completed = uniqueGroupRows.filter(isCompleted);
  const cancelled = uniqueGroupRows.filter(isCancelled);
  const noShows = uniqueGroupRows.filter(isNoShow);
  console.log(completed,cancelled,noShows,"completed,cancelled,noShows---")

  const totalBookings = uniqueGroupRows.length;
  const totalJobsCompleted = completed.length;
console.log(totalBookings, totalJobsCompleted, "totalBookings, totalJobsCompleted---")

  // ── Revenue ──────────────────────────────────────────────────────
  const totalRevenue = sum(rows, "received_amount");
  const revenuePerBooking = totalBookings ? fixed(totalRevenue / totalBookings, 2) : 0;
  const avgReceivedAll = totalBookings ? totalRevenue / totalBookings : 0;

  // ── Crew / Providers ─────────────────────────────────────────────
  const allProviderKeys = [...new Set(rows.filter(r => providerKey(r)).map(r => providerKey(r)))];
  const activeProviderKeys = [...new Set(completed.filter(r => providerKey(r)).map(r => providerKey(r)))];
  const totalCrew = allProviderKeys.length || 1;
  const activeCrew = activeProviderKeys.length;
  const activeCrewPct = fixed((activeCrew / totalCrew) * 100);

  // ── Revenue per crew ─────────────────────────────────────────────
  const crewRevMap = {};
  rows.forEach(r => {
    const key = providerKey(r);
    const name = providerName(r);
    if (!key) return;
    if (!crewRevMap[key]) crewRevMap[key] = { revenue: 0, jobs: 0, name };
    crewRevMap[key].revenue += toNum(r.received_amount);
    crewRevMap[key].jobs++;
    // Always update to best available name
    if (name && !isNumeric(name) && name !== `Crew #${key}`) {
      crewRevMap[key].name = name;
    }
  });

  const revenuePerCrewAvg = totalCrew ? fixed(totalRevenue / totalCrew, 2) : 0;

  const revenuePerCrewBreakdown = Object.entries(crewRevMap)
    .map(([id, v]) => ({
      crew_id: id,
      crew_name: v.name,
      current: v.revenue,
      jobs: v.jobs,
    }))
    .sort((a, b) => b.current - a.current);

  // ── Working days ─────────────────────────────────────────────────
  const distinctDays = [...new Set(
    completed.map(r => {
      const d = localServiceDate(r.service_date);
      return d ? localYMD(d) : null;
    }).filter(Boolean)
  )];
  const workingDays = distinctDays.length || 1;
  const avgJobsPerDay = fixed(totalJobsCompleted / workingDays);
  const weeksSpanned = Math.max(workingDays / 7, 1);

  // ── Jobs per crew ─────────────────────────────────────────────────
  const jobsPerCrewDaily = activeCrew ? fixed(totalJobsCompleted / activeCrew / workingDays) : 0;
  const jobsPerCrewWeekly = activeCrew ? fixed(totalJobsCompleted / activeCrew / weeksSpanned) : 0;

  // ── Scheduling rates ─────────────────────────────────────────────
  const completedVsScheduledPct = totalBookings ? fixed((completed.length / totalBookings) * 100) : 0;
  const cancellationRatePct = totalBookings ? fixed((cancelled.length / totalBookings) * 100) : 0;
  const noShowRatePct = totalBookings ? fixed((noShows.length / totalBookings) * 100) : 0;

  // ── Bookings by day of week (Mon–Sun) ────────────────────────────
  const byDay = {};
  uniqueGroupRows.forEach(r => {
    const d = localServiceDate(r.service_date);
    if (!d) return;
    const name = DAY_NAMES[d.getDay()];
    byDay[name] = (byDay[name] || 0) + 1;
  });
  const bookingsByDay = ORDERED_DAYS.map(day => ({ day, count: byDay[day] || 0 }));

  // ── Peak day ─────────────────────────────────────────────────────
  const peakDayEntry = bookingsByDay.reduce(
    (m, d) => d.count > m.count ? d : m,
    bookingsByDay[0] || { day: "—", count: 0 }
  );
  const peakDay = peakDayEntry.day;

  // ── Peak time ─────────────────────────────────────────────────────
  const byCompletionHour = {};
  rows.forEach(r => {
    const h = getCompletionHour(r);
    if (h == null || isNaN(h)) return;
    byCompletionHour[h] = (byCompletionHour[h] || 0) + 1;
  });
  const peakHourEntry = Object.entries(byCompletionHour).sort((a, b) => b[1] - a[1])[0];
  const peakTime = peakHourEntry
    ? `${parseInt(peakHourEntry[0]) % 12 || 12}:00 ${parseInt(peakHourEntry[0]) >= 12 ? "PM" : "AM"}`
    : "—";

  // ── Customers ────────────────────────────────────────────────────
  const customerCounts = {};
  uniqueGroupRows.forEach(r => {
    if (r.customer) customerCounts[r.customer] = (customerCounts[r.customer] || 0) + 1;
  });
  const totalCustomers = Object.keys(customerCounts).length;
  const repeatCustomers = Object.values(customerCounts).filter(c => c >= 2).length;
  const repeatCustomersPct = totalCustomers ? fixed((repeatCustomers / totalCustomers) * 100) : 0;
  const bookingsPerCustomer = totalCustomers ? fixed(totalBookings / totalCustomers, 2) : 0;

  // ── Loss / Leakage ───────────────────────────────────────────────
  const lostRevenueNoShows = fixed(noShows.length * avgReceivedAll, 2);
  const lostRevenueCancellations = fixed(cancelled.length * avgReceivedAll, 2);

  // ── Crew breakdown table ─────────────────────────────────────────
  const crewJobMap = {};
  rows.forEach(r => {
    const key = providerKey(r);
    const name = providerName(r);
    if (!key) return;
    if (!crewJobMap[key]) {
      crewJobMap[key] = {
        crew_id: key,
        crew_name: name,
        service_provider_name: r.service_provider_name || name, // expose raw field too
        jobs: 0,
        revenue: 0,
      };
    }
    crewJobMap[key].jobs++;
    // Keep updating to best name
    if (name && !isNumeric(name) && name !== `Crew #${key}`) {
      crewJobMap[key].crew_name = name;
      crewJobMap[key].service_provider_name = r.service_provider_name || name;
    }
  });
  Object.keys(crewRevMap).forEach(key => {
    if (crewJobMap[key]) crewJobMap[key].revenue = crewRevMap[key].revenue;
  });
  const crewBreakdown = Object.values(crewJobMap).sort((a, b) => b.revenue - a.revenue);

  return {
    // Operations & Crew
    totalBookings,
    totalJobsCompleted,
    avgJobsPerDay,
    jobsPerCrewDaily,
    jobsPerCrewWeekly,
    activeCrew,
    totalCrew,
    activeCrewPct,
    // Scheduling
    completedVsScheduledPct,
    cancellationRatePct,
    noShowRatePct,
    bookingsByDay,
    peakDay,
    peakTime,
    // Revenue
    totalRevenue,
    revenuePerBooking,
    revenuePerCrewAvg,
    revenuePerCrewBreakdown,
    // Customers
    totalCustomers,
    repeatCustomersPct,
    bookingsPerCustomer,
    // Loss / Leakage
    lostRevenueNoShows,
    lostRevenueCancellations,
    noShowCount: noShows.length,
    cancelledCount: cancelled.length,
    // Crew table
    crewBreakdown,
    // Raw counts
    raw: {
      completedCount: completed.length,
      cancelledCount: cancelled.length,
      noShowCount: noShows.length,
    },
  };
}